/**
 * September cross-product color picker
 * Loaded on all pages (layout/theme.liquid).
 *
 * Replaces the standard single-color swatch with cross-product color swatches
 * sourced from the color DB. Selecting a different color navigates to that
 * product via the existing product-info.js swap mechanism.
 *
 * Re-initializes after cross-product navigation by observing the parent of
 * <main> — HTMLUpdateUtility.viewTransition inserts the new <main> as a sibling,
 * so the observer must watch the parent container, not <main> itself.
 */
(function () {
  'use strict';

  var DB_URL    = '/apps/as-seen-insta/json/db-colors-v3.json';
  var CACHE_KEY = 'sept-color-db-v3';
  var CACHE_TTL = 8 * 60 * 60 * 1000; // 8 hours
  var _dbPromise = null;

  function fetchDb() {
    if (!_dbPromise) {
      try {
        var cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached && cached.ts && (Date.now() - cached.ts) < CACHE_TTL) {
          _dbPromise = Promise.resolve(cached.data);
          return _dbPromise;
        }
      } catch (e) {}

      _dbPromise = fetch(DB_URL)
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (data) {
          try { localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data })); } catch (e) {}
          return data;
        })
        .catch(function (err) {
          console.warn('[sept] DB fetch failed:', err);
          return null;
        });
    }
    return _dbPromise;
  }

  function getAssetBase() {
    var scripts = document.querySelectorAll('script[src]');
    for (var i = 0; i < scripts.length; i++) {
      if (scripts[i].src.indexOf('september-color-picker.js') !== -1) {
        return scripts[i].src.replace(/september-color-picker\.js.*$/, '');
      }
    }
    return '';
  }

  function findColorFieldset(variantSelects) {
    // Try CSS class first (when color_swatch_trigger is configured)
    var el = variantSelects.querySelector('.product-form__input--color');
    if (el) return el;

    // Fallback: find by legend text
    var spans = variantSelects.querySelectorAll('[data-selected-swatch-value]');
    for (var i = 0; i < spans.length; i++) {
      var name = (spans[i].dataset.selectedSwatchValue || '').toLowerCase();
      if (name === 'color' || name === 'colour') {
        var fs = spans[i].closest('fieldset') || spans[i].closest('.product-form__input');
        if (fs) return fs;
      }
    }
    return null;
  }

  function findStyleKey(handle, products) {
    var keys = Object.keys(products).sort(function (a, b) { return b.length - a.length; });
    for (var i = 0; i < keys.length; i++) {
      if (handle === keys[i] || handle.indexOf(keys[i] + '-') === 0) return keys[i];
    }
    return null;
  }

  function colorFromHandle(handle, styleKey) {
    return handle.slice(styleKey.length + 1);
  }

  function buildSwatches(fieldset, colors, styleKey, currentColor, assetBase) {
    var existingInput = fieldset.querySelector('input[type="radio"]');
    if (!existingInput) return;

    var inputName  = existingInput.name;
    var formId     = existingInput.getAttribute('form');

    var fragment = document.createDocumentFragment();

    colors.forEach(function (entry, idx) {
      var color         = entry.color;
      var coloricon     = entry.coloricon;
      var originalColor = entry.originalColor;
      var isCurrent     = color === currentColor;
      var inputId       = 'sept-color-' + idx;
      var imgUrl        = assetBase + coloricon + '.png';

      var input = document.createElement('input');
      input.type  = 'radio';
      input.id    = inputId;
      input.name  = inputName;
      input.value = originalColor;
      if (formId) input.setAttribute('form', formId);
      if (isCurrent) input.checked = true;
      input.dataset.optionValueId = 'sept-' + color;
      if (!isCurrent) input.dataset.productUrl = '/products/' + styleKey + '-' + color;
      input.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);';

      var label = document.createElement('label');
      label.htmlFor = inputId;
      label.className = 'sept-color-swatch';
      label.style.cssText = [
        'background-image:url(' + imgUrl + ')',
        'background-size:cover',
        'background-position:center',
        'background-color:#e0e0e0',
      ].join(';');
      label.setAttribute('title', originalColor);
      label.setAttribute('aria-label', originalColor);

      if (isCurrent) {
        label.style.boxShadow = 'inset 0 0 0 1px #000, inset 0 0 0 3px #fff';
      }

      var tooltip = document.createElement('span');
      tooltip.className = 'tooltip pointer-events-none';
      tooltip.textContent = originalColor;
      label.appendChild(tooltip);

      fragment.appendChild(input);
      fragment.appendChild(label);
    });

    Array.from(fieldset.querySelectorAll('input[type="radio"], label')).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    fieldset.appendChild(fragment);
    fieldset.style.opacity = '1';

    var currentEntry = colors.find(function (e) { return e.color === currentColor; });
    if (currentEntry) {
      var valueSpan = fieldset.querySelector('[data-selected-swatch-value]');
      if (valueSpan) valueSpan.textContent = currentEntry.originalColor;
    }
  }

  function sortColorsByNewest(colors, colorsIndexed) {
    if (!colorsIndexed) return colors;
    return colors.slice().sort(function (a, b) {
      var ai = (colorsIndexed[a.color] || {}).index || 0;
      var bi = (colorsIndexed[b.color] || {}).index || 0;
      return bi - ai;
    });
  }

  function init() {
    var variantSelects = document.querySelector('variant-selects[data-url]');
    if (!variantSelects) return;

    var dbFetch       = fetchDb();
    var assetBase     = getAssetBase();
    var productHandle = (variantSelects.dataset.url || '').replace(/^\/products\//, '').split('?')[0];
    if (!productHandle) return;

    var colorFieldset = findColorFieldset(variantSelects);
    if (!colorFieldset) return;

    colorFieldset.style.opacity = '0';

    dbFetch.then(function (db) {
      if (!db || !db.shopifyProducts) {
        colorFieldset.style.opacity = '1';
        return;
      }

      var styleKey = findStyleKey(productHandle, db.shopifyProducts);
      if (!styleKey) {
        colorFieldset.style.opacity = '1';
        return;
      }

      var colors = db.shopifyProducts[styleKey];
      if (!colors || colors.length < 1) {
        colorFieldset.style.opacity = '1';
        return;
      }

      colors = sortColorsByNewest(colors, db.shopifyColorsIndexed);
      var currentColor = colorFromHandle(productHandle, styleKey);
      buildSwatches(colorFieldset, colors, styleKey, currentColor, assetBase);
    });
  }

  function initCollection() {
    var cards = document.querySelectorAll('.product-card');
    console.log('[sept] initCollection: found', cards.length, 'cards');
    if (!cards.length) return;

    fetchDb().then(function (db) {
      if (!db || !db.shopifyProducts) return;
      var assetBase = getAssetBase();

      Array.from(cards).forEach(function (card) {
        // Get the product handle from the main card link (image or title).
        var link = card.querySelector('a[href^="/products/"]');
        if (!link) return;
        var handle = link.getAttribute('href').replace(/^\/products\//, '').split('?')[0];
        if (!handle) return;

        var styleKey = findStyleKey(handle, db.shopifyProducts);
        if (!styleKey) return;

        var colors = db.shopifyProducts[styleKey];
        if (!colors || colors.length < 1) return;

        colors = sortColorsByNewest(colors, db.shopifyColorsIndexed);
        var currentColor = colorFromHandle(handle, styleKey);

        // Find or create the swatch container inside the card info area.
        var info = card.querySelector('.product-card__info');
        if (!info) return;

        var container = info.querySelector('.product-card__swatches');
        if (!container) {
          container = document.createElement('div');
          container.className = 'product-card__swatches flex flex-wrap items-center gap-2';
          info.appendChild(container);
        }

        buildCollectionSwatches(card, container, colors, styleKey, currentColor, assetBase);
      });
    });
  }

  function buildCollectionSwatches(card, container, colors, styleKey, currentColor, assetBase) {
    var ul = container.querySelector('ul.swatches--product-card');
    if (!ul) {
      ul = document.createElement('ul');
      ul.className = 'swatches swatches--product-card flex flex-wrap items-center';
      container.innerHTML = '';
      container.appendChild(ul);
    }

    // Respect the theme's pcard_maximum_swatches_to_show setting.
    var maxSwatches = (window.FoxTheme && window.FoxTheme.settings && window.FoxTheme.settings.pcardMaxSwatchesToShow) || Infinity;
    var visibleColors = colors.slice(0, maxSwatches);
    var hiddenCount = Math.max(0, colors.length - maxSwatches);

    // Always ensure the current color is visible — swap it into the last slot if needed.
    var currentInVisible = visibleColors.some(function (e) { return e.color === currentColor; });
    if (!currentInVisible && hiddenCount > 0) {
      var currentEntry = colors.find(function (e) { return e.color === currentColor; });
      if (currentEntry) visibleColors[visibleColors.length - 1] = currentEntry;
    }

    var fragment = document.createDocumentFragment();

    visibleColors.forEach(function (entry) {
      var color         = entry.color;
      var coloricon     = entry.coloricon;
      var originalColor = entry.originalColor;
      var isCurrent     = color === currentColor;
      var imgUrl        = assetBase + coloricon + '.png';

      var li = document.createElement('li');
      li.setAttribute('data-tooltip', '');

      var a = document.createElement('a');
      a.href = '/products/' + styleKey + '-' + color;
      a.className = 'swatch-color block relative';
      a.setAttribute('title', originalColor);
      a.setAttribute('aria-label', originalColor);
      a.style.cssText = [
        'background-image:url(' + imgUrl + ')',
        'background-size:cover',
        'background-position:center',
        'background-color:#e0e0e0',
      ].join(';');

      if (isCurrent) {
        a.style.boxShadow = 'inset 0 0 0 1px #000, inset 0 0 0 3px #fff';
      }

      // Open the quick-view modal for this color instead of navigating directly.
      // QuickViewModal.quickview() always re-fetches from data-product-url, so
      // we just update that attribute before triggering the existing button.
      (function (productUrl) {
        a.addEventListener('click', function (e) {
          var modal = card.querySelector('quick-view-modal');
          if (!modal) return; // no modal — let href navigate normally
          e.preventDefault();

          modal.dataset.productUrl = productUrl;
          // Clear previous content so the old product doesn't flash before the new one loads.
          var content = modal.querySelector('.quick-view__content');
          if (content) content.innerHTML = '';
          modal.classList.remove('quick-view-loaded');

          // Fire the existing trigger so DrawerComponent runs its open logic.
          var trigger = card.querySelector('[aria-controls="' + modal.id + '"]');
          if (trigger) trigger.click();
        });
      })('/products/' + styleKey + '-' + color);

      var tooltip = document.createElement('span');
      tooltip.className = 'tooltip pointer-events-none';
      tooltip.textContent = originalColor;
      a.appendChild(tooltip);

      li.appendChild(a);
      fragment.appendChild(li);
    });

    ul.innerHTML = '';
    ul.appendChild(fragment);

    // Remove any existing overflow link before (re)rendering.
    var existingOverflow = container.querySelector('a.reversed-link');
    if (existingOverflow) existingOverflow.parentNode.removeChild(existingOverflow);

    if (hiddenCount > 0) {
      var more = document.createElement('a');
      more.href = '/products/' + styleKey + '-' + currentColor;
      more.className = 'reversed-link';
      more.textContent = '+' + hiddenCount;
      more.addEventListener('click', function (e) {
        var modal = card.querySelector('quick-view-modal');
        if (!modal) return;
        e.preventDefault();
        var trigger = card.querySelector('[aria-controls="' + modal.id + '"]');
        if (trigger) trigger.click();
      });
      container.appendChild(more);
    }
  }

  function watchForProductSwaps() {
    var main = document.querySelector('main');
    if (!main) return;
    // viewTransition inserts the new <main> as a sibling (before the old one),
    // so we observe the parent container, not <main> itself.
    var container = main.parentNode || document.body;
    var lastUrl = null;
    var vs = document.querySelector('variant-selects[data-url]');
    if (vs) lastUrl = vs.dataset.url;

    new MutationObserver(function () {
      var vsNow = document.querySelector('variant-selects[data-url]');
      if (!vsNow || vsNow.dataset.url === lastUrl) return;
      lastUrl = vsNow.dataset.url;
      init();
    }).observe(container, { childList: true, subtree: false });
  }

  // Re-run init() whenever a quick-view modal finishes loading its product content.
  // Also watch for within-modal variant changes: product-info.js replaces product-info
  // via viewTransition (inserts new sibling), exactly like the main product page swap.
  var _quickViewObserver = null;
  document.addEventListener('quick-view:loaded', function () {
    init();

    if (_quickViewObserver) { _quickViewObserver.disconnect(); _quickViewObserver = null; }

    var vs = document.querySelector('.quick-view__content variant-selects[data-url]');
    if (!vs) return;
    var productInfo = vs.closest('product-info');
    if (!productInfo || !productInfo.parentNode) return;

    // viewTransition inserts the new product-info as a sibling — observe its parent.
    _quickViewObserver = new MutationObserver(function () {
      if (document.querySelector('.quick-view__content variant-selects[data-url]')) init();
    });
    _quickViewObserver.observe(productInfo.parentNode, { childList: true, subtree: false });
  });

  // Re-init after same-product option changes (e.g., size selection) that replace
  // the variant-selects DOM via viewTransition without changing the product URL.
  document.addEventListener('variant-selects:updated', function () { init(); });

  // Re-init collection swatches after load-more or facet filter changes.
  document.addEventListener('collection:rerendered', function () {
    console.log('[sept] collection:rerendered heard, calling initCollection');
    initCollection();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); watchForProductSwaps(); initCollection(); });
  } else {
    init();
    watchForProductSwaps();
    initCollection();
  }
})();
